import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import axios from 'axios';
import { noop } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { CreateContactDialog } from '~/components/Contacts/UpsertContact/Create/CreateContactDialog';
import AlertBanner from '~/components/core/AlertBanner';
import FormControlLabel from '~/components/core/Atomic/FormControlLabel';
import Grid from '~/components/core/Atomic/Grid/Grid';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import { CONFIGURABLE_FNOL_POLICY_TYPES, fetchCreateLikeDraft } from '~/components/Fnol/createLikeDraftUtils';
import { FnolAiInboxContainer } from '~/components/Fnol/FnolAiInbox/FnolAiInboxContainer';
import { useFnolAiPermissions } from '~/components/Fnol/FnolAiInbox/hooks/useFnolAiPermissions';
import { ActivePolicyCategoriesProvider } from '~/components/hooks/useActivePolicyCategories';
import Checkbox from '~/components/ui/atoms/Checkbox';

import { FNOL_MIXPANEL_EVENTS } from '../../pocs/mixpanel';
import { CONFIGURATION_FEATURES_NAMES } from '../../Types';
import { isFeatureEnabled, reportAxiosError } from '../../Utils';
import AutoFnol from '../Auto/AutoFnol';
import CardDialog from '../CardDialog';
import mixpanel from '../CmsMain/mixpanel';
import ContactsContextProvider, { useContacts } from '../ContactsContext';
import { useCms } from '../hooks/useCms';
import useCurrentUserRoles from '../hooks/useCurrentUserRoles';
import useOrganization from '../OrganizationContext';
import { PolicyContextProvider, usePolicy } from '../PolicyContainer';
import PolicySearchContainer, { checkPolicyPreDefinedRequiredFields } from '../PolicySearch';
import { hasPolicySource } from '../policySearchSource';
import { ShowOnlyTextField } from '../TextFieldFormik';

import NewFnolScreen from './NewFnolUI/FnolScreen';
import { OrganizationSelectDialog } from './FnolContainer';
import FnolDraftsContainer from './FnolDraft';
import HomeFnolPage from './HomeFnolPage';
import LossWithoutPolicyCard from './LossWithoutPolicyCard';

import { useStyles } from '../../assets/styles';

/**
 * Extract inline contact objects from draft/form values.
 *
 * The backend (add_contact_full_name_recursive) and the frontend (ContactTextFieldFormik.onSelectContact)
 * both store a full contact object at `*_contact` next to every `*_contact_id` field.
 * These contacts may not be in policy.contacts (e.g. AI-created entities, or contacts added in a previous
 * draft session). This function extracts them so they can be merged into the ContactsContext.
 */
function extractContactsFromDraftValues(obj, seen = new Set()) {
  if (!obj || typeof obj !== 'object') return [];
  if (Array.isArray(obj)) return obj.flatMap((item) => extractContactsFromDraftValues(item, seen));

  const contacts = [];
  for (const key of Object.keys(obj)) {
    // Convention: *_contact_id has a sibling *_contact object
    if (key.endsWith('_contact_id') && obj[key]) {
      const contactKey = key.slice(0, -'_id'.length); // e.g. reporter_contact
      const contact = obj[contactKey];
      if (contact?.id && !seen.has(contact.id)) {
        seen.add(contact.id);
        contacts.push(contact);
      }
    }
    // Recurse into nested objects/arrays (but skip the contact objects themselves)
    if (typeof obj[key] === 'object' && obj[key] !== null && !key.endsWith('_contact')) {
      contacts.push(...extractContactsFromDraftValues(obj[key], seen));
    }
  }
  return contacts;
}

/**
 * Wraps FNOL content with a ContactsContext that includes both policy contacts
 * and contacts extracted from draft form values (e.g. AI-created or previously saved contacts).
 * Must be rendered inside PolicyContextProvider.
 */
function FnolContactsProvider({ fnolDraft, children }) {
  const { policy } = usePolicy();
  const { contactsOrganizationId, rolesClaimType } = useContacts();

  const draftContacts = useMemo(() => {
    if (!fnolDraft) return [];
    const all = extractContactsFromDraftValues(fnolDraft.draft);
    const policyIds = new Set(policy.contacts?.map((c) => c.id) || []);
    return all.filter((c) => !policyIds.has(c.id));
  }, [fnolDraft, policy.contacts]);

  const [contacts, setContacts] = useState([...(policy.contacts || []), ...draftContacts]);

  const handleAddContact = (contact) => {
    setContacts((prev) => [...prev, contact]);
  };

  return (
    <ContactsContextProvider
      contacts={contacts}
      onAddContact={handleAddContact}
      contactsOrganizationId={contactsOrganizationId}
      rolesClaimType={rolesClaimType}
    >
      {children}
    </ContactsContextProvider>
  );
}

FnolContactsProvider.propTypes = {
  fnolDraft: PropTypes.object,
  children: PropTypes.node.isRequired,
};

export const trackOldFnolClick = (policy, fnol_type, reported_date) => {
  mixpanel.track(FNOL_MIXPANEL_EVENTS.CREATE_FNOL_CLICKED, {
    reporting_type: policy.is_manual ? 'reported without policy' : 'reported from existing policy',
    fnol_type,
    reported_date,
  });
};

const SUPPORTED_POLICIES = ['home_policy', 'auto_policy', 'pet_policy', 'general_policy'];
const INITIAL_COMMUNICATION_TEXT =
  'We are terribly sorry to hear about your loss. Dealing with something like this is never easy. We are here to provide you assistance and help you get through this trying time.\n\n We need to advise you that we cannot commit to coverage for your claim until a full investigation has been completed, however you should take reasonable to steps to mitigate any further damage';
const EXPRESSIONS_TEXT = (
  <span>
    I expressed <strong>sympathy</strong>, qualified <strong>coverage</strong> and advised insured to{' '}
    <strong>mitigate</strong> damages
  </span>
);

function FnolScreen({ onFnolInboxExpand = noop }) {
  const history = useHistory();
  const location = useLocation();
  const { classes } = useStyles();
  const { user, userOrganization } = useCms();
  const { isPolicyAPIEnabled, isFnolInboxConfigured, policySearchConfig } = useOrganization();
  const { isAllowedForUser: isFnolAiInboxAllowedForUser } = useFnolAiPermissions();

  const [expressionVerified, setExpressionVerified] = useState(false);
  const [manuallyFillingInsuredDetails, setManuallyFillingInsuredDetails] = useState(false);
  const [manuallyFillingOrganization, setManuallyFillingOrganization] = useState(false);
  const [manualPolicyType, setManualPolicyType] = useState(undefined);
  const [manualPolicyLob, setManualPolicyLob] = useState(undefined);
  const [manualPolicyOrganization, setManualPolicyOrganization] = useState(
    user.is_super_organization ? undefined : userOrganization
  );
  const [fnolDraft, setFnolDraft] = useState(undefined);
  const [savedFnolDraftId, setSavedFnolDraftId] = useState(undefined);
  const [communicationId, setCommunicationId] = useState(undefined);
  const [fnolAiRecommendationId, setFnolAiRecommendationId] = useState(undefined);
  const [mixpanelExtra, setMixpanelExtra] = useState(null);
  const [hasFnolAiRecommendations, setHasFnolAiRecommendations] = useState(false);

  // Create Like entry: the claim actions menu navigates here with the source claim in route state.
  // Mirrored into component state so it can be dropped on submit while this component stays mounted —
  // a phone rep is returned to policy search instead of the new claim.
  const [createLikeContext, setCreateLikeContext] = useState(location.state?.createLikeContext);
  // The route's own Create Like context as of the latest commit. Component state catches up a render
  // after a route change, so async Create Like work checks this before applying what it fetched.
  const routeCreateLikeContextRef = useRef(location.state?.createLikeContext);
  // "Use existing policy" reports under the source claim's own policy, loaded in place of the search;
  // the search comes back only if that policy cannot be used.
  const [hasExistingPolicyLoadFailed, setHasExistingPolicyLoadFailed] = useState(false);
  const [chosenPolicyAndClaimValues, setChosenPolicyAndClaimValues] = useState(null);
  const [selectedSubOrgId, setSelectedSubOrgId] = useState(undefined);
  const [showLossWithoutPolicyCard, setShowLossWithoutPolicyCard] = useState(true);
  const [showCliveWarning, setShowCliveWarning] = useState(false);
  // Create Like fetches the draft before the policy is applied, so the policy list has to be held
  // for the round-trip. The ref is the re-entrancy guard (two clicks in one render both read the
  // same stale state); the state drives the disabled affordance.
  const [isFetchingDraft, setIsFetchingDraft] = useState(false);
  const isFetchingDraftRef = useRef(false);
  const isConfigurableFnolEnabled = isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.CONFIGURABLE_FNOL);

  const { isUserPhoneRep, isUserFromSuperOrganization } = useCurrentUserRoles();
  // A phone rep picks no policy, searched or reused, before confirming the initial communication.
  const isAwaitingExpressions = isUserPhoneRep && !expressionVerified;
  const isLoadingExistingPolicy =
    !!createLikeContext?.useExistingPolicy && !hasExistingPolicyLoadFailed && !isAwaitingExpressions;

  const handleLossWithoutPolicy = ({ subOrgId, lob }) => {
    setManualPolicyLob(lob);
    if (isConfigurableFnolEnabled) {
      if (lob === 'auto_claim') {
        setManualPolicyType('auto_policy');
      } else {
        setManualPolicyType('general_policy');
      }
    } else {
      setManualPolicyType(lob.replace('_claim', '_policy'));
    }
    setSelectedSubOrgId(subOrgId);
    setManuallyFillingInsuredDetails(true);
    setShowLossWithoutPolicyCard(false);
  };

  // Every entry point that reports under the draft's own policy lands here: the saved-drafts table,
  // the policy-search draft picker, and the AI inbox when it has no real policy. The draft and the
  // policy go into one update on purpose — FnolContactsProvider seeds its contacts in a mount-only
  // useState initializer and NewFnolUI's Formik has no enableReinitialize, so a draft that arrives
  // in a later render than the policy is dropped by both.
  const applyDraftWithItsOwnPolicy = (fnolDraft) => {
    setFnolDraft(fnolDraft);
    setSavedFnolDraftId(fnolDraft.id);
    setChosenPolicyAndClaimValues({
      // Nested under `policy` on a configurable-FNOL draft, the draft itself on the legacy one.
      policy: isConfigurableFnolEnabled ? fnolDraft.draft.policy : fnolDraft.draft,
      claimValues: {},
    });
    setManuallyFillingInsuredDetails(false);
  };

  // Create Like: the source claim's draft for the policy the new claim will be reported under, with
  // the dialog's choices applied. Returned rather than set, so each caller can set it in the same
  // update as that policy — see applyDraftWithItsOwnPolicy.
  const fetchCreateLikeDraftForPolicy = async (policy) => {
    const draft = await fetchCreateLikeDraft(createLikeContext.sourceClaimId, policy);
    // The backend includes the source's external claim numbers whenever the target renders the
    // section; the dialog's checkbox decides whether they are applied. Stripped here, where the
    // choice and the draft meet. Only an explicit `false` strips them: copying is the default,
    // so route state that predates this flag must not silently drop them.
    if (draft && createLikeContext.copyExternalClaimNumbers === false) {
      delete draft.external_claim_numbers;
    }
    return draft;
  };

  const handleSelectPolicy = (policy, claimValues) => {
    setChosenPolicyAndClaimValues({ policy, claimValues });
    mixpanel.track(FNOL_MIXPANEL_EVENTS.REPORT_A_NEW_LOSS_CLICKED_WITH_POLICY_FNOL);
  };

  // Resolves to whether the policy was applied. `isPolicyReused` is the source claim's own policy,
  // taken with no search, so no policy-selection click is tracked for it.
  const handleCreateLikeSelectPolicy = async (policy, claimValues, { isPolicyReused = false } = {}) => {
    if (isFetchingDraftRef.current) {
      return false;
    }
    isFetchingDraftRef.current = true;
    setIsFetchingDraft(true);
    try {
      const draft = await fetchCreateLikeDraftForPolicy(policy);
      // The user left for another FNOL while the draft was fetched: neither the draft nor the policy
      // belongs to that report.
      if (routeCreateLikeContextRef.current !== createLikeContext) {
        return false;
      }
      if (draft) {
        setFnolDraft({ draft });
      }
      // Both land in one update, which is what FnolContactsProvider's mount-only contacts
      // initializer and NewFnolUI's Formik (no enableReinitialize) need — see
      // applyDraftWithItsOwnPolicy. This path supplies the searched policy, or the source claim's
      // own, rather than the draft's.
      if (isPolicyReused) {
        setChosenPolicyAndClaimValues({ policy, claimValues });
      } else {
        handleSelectPolicy(policy, claimValues);
      }
      return true;
    } finally {
      isFetchingDraftRef.current = false;
      setIsFetchingDraft(false);
    }
  };

  // What the policy search does with a database hit before handing it back: fetch the whole policy,
  // attach its sub-organization and run the required-fields pre-check. `policy` is null when the
  // policy cannot be used, which sends the user to the search instead; `missingFieldDescs` says why
  // when the pre-check is the reason, for the caller to show once it knows the report is still open.
  const loadExistingPolicy = async (policyId) => {
    let policy;
    try {
      const response = await axios.get(`/api/v1/policies/${policyId}`);
      policy = response.data;
    } catch (error) {
      // No error dialog, as for a failed draft fetch: the search is the fallback.
      await reportAxiosError(error, false);
      return { policy: null };
    }
    // Create Like runs on configurable FNOL, which has no form for a source claim on a legacy line
    // of business; the search only ever offers the organization's own.
    if (!CONFIGURABLE_FNOL_POLICY_TYPES.includes(policy.type)) {
      return { policy: null };
    }
    // The response has the sub-organization id only; the draft fetch and the submit read the object.
    const subOrgId = userOrganization.sub_organizations_enabled ? policy.sub_organization_id : null;
    if (subOrgId) {
      policy.sub_organization = userOrganization.sub_organizations.find((subOrg) => subOrg.id === subOrgId);
    }
    const preCheck = await checkPolicyPreDefinedRequiredFields({ policyData: policy, subOrgId });
    if (preCheck.status === 'rejected') {
      return { policy: null };
    }
    if (preCheck.status === 'ok' && preCheck.missing.length > 0) {
      return { policy: null, missingFieldDescs: preCheck.missing.map((field) => field.desc).join(', ') };
    }
    return { policy };
  };

  const handleFnolSubmitted = (claimId) => {
    if (savedFnolDraftId) {
      // best effort, we don't wait and don't check for the return value
      axios.delete(`/api/v1/fnol_drafts/${savedFnolDraftId}`);
    }
    // A phone rep is deliberately not navigated away, so everything this report accumulated has to
    // be cleared or it carries into their next one. The draft id is no longer safe to reuse once
    // the delete above has been issued, and the inbox ids would send the next FNOL to the previous
    // email's endpoint (NewFnolUI/FnolScreen sends to /fnol_inbox/inbox/<id>/fnol whenever
    // fnolAiRecommendationId is set) and seed it with the old sub-organization, while the Clive
    // warning and mixpanel payload would attribute the new report to the previous email.
    setFnolDraft(undefined);
    setSavedFnolDraftId(undefined);
    setShowLossWithoutPolicyCard(true);
    setChosenPolicyAndClaimValues(null);
    setExpressionVerified(false);
    setHasFnolAiRecommendations(false);
    setFnolAiRecommendationId(undefined);
    setCommunicationId(undefined);
    setSelectedSubOrgId(undefined);
    setMixpanelExtra(null);
    setShowCliveWarning(false);
    setCreateLikeContext(undefined);
    if (!isUserPhoneRep) {
      history.push(`/claims/${claimId}`);
    }
  };

  const handlePolicyNotFound = (policyType) => {
    setManualPolicyType(policyType);
    setManualPolicyLob(policyType);
    if (isUserFromSuperOrganization) {
      setManuallyFillingOrganization(true);
    } else {
      setManuallyFillingInsuredDetails(true);
    }
  };

  const handleSubmitDraft = async (values) => {
    try {
      if (isPolicyAPIEnabled || isConfigurableFnolEnabled) {
        if (savedFnolDraftId) {
          await axios.put(`/api/v1/fnol_drafts/${savedFnolDraftId}`, values);
        } else {
          const res = await axios.post('/api/v1/fnol_drafts', values);
          setSavedFnolDraftId(res.data.id);
        }
      } else {
        await axios.post(`/api/v1/claims/fnol/drafts/${values['policy_id']}`, values);
      }
    } catch (error) {
      reportAxiosError(error);
    }
  };

  const handleDraftSelected = (fnolDraft, selectionSource) => {
    mixpanel.track(FNOL_MIXPANEL_EVENTS.LOAD_DRAFT_CLICKED, {
      draft_selection_source: selectionSource,
    });
    applyDraftWithItsOwnPolicy(fnolDraft);
  };

  const handleFnolAiInboxNewLoss = ({
    fnolDraft,
    communicationId,
    fnolAiRecommendationId,
    policyData,
    subOrgId,
    lob,
    mixpanelExtra,
    hasAiRecommendations,
    isCliveEnabled,
  }) => {
    setCommunicationId(communicationId);
    setFnolAiRecommendationId(fnolAiRecommendationId);
    setMixpanelExtra(mixpanelExtra);
    setHasFnolAiRecommendations(hasAiRecommendations || false);

    if (policyData && !policyData.is_manual) {
      // Real policy from the policy API — report under that, not under the policy the draft
      // carries, which the server only materialized to align the ids the draft holds.
      if (fnolDraft) {
        setFnolDraft(fnolDraft);
        setSavedFnolDraftId(fnolDraft.id);
      }
      handleSelectPolicy(policyData, {});
      setShowCliveWarning(isCliveEnabled);
    } else if (fnolDraft && isConfigurableFnolEnabled) {
      // CASE 1/2: a draft but no real policy. Report under the policy the draft carries — the
      // manual one the server builds around the insured the AI extracted. InsuredDetailsFormDialog
      // opens blank, so sending the user there would mean re-typing what the draft already holds.
      // The policyholder section seeds `verified_insured_info` from `policy.is_manual`, so the
      // insured arrives pre-confirmed — the draft is the only review this flow gets.
      applyDraftWithItsOwnPolicy(fnolDraft);
      setSelectedSubOrgId(subOrgId ?? fnolDraft.sub_organization_id);
      setShowLossWithoutPolicyCard(false);
      setShowCliveWarning(isCliveEnabled);
    } else if (fnolDraft) {
      // CASE 1/2 without configurable FNOL: the draft is the converter's FE dict, which has no
      // `type` and would fail the SUPPORTED_POLICIES check, so keep the manual-intake dialog.
      setFnolDraft(fnolDraft);
      setSavedFnolDraftId(fnolDraft.id);

      const effectiveLob = policyData?.lob || fnolDraft.policy_lob || lob || 'general_claim';
      const effectiveSubOrgId = subOrgId ?? fnolDraft.sub_organization_id;
      handleLossWithoutPolicy({ subOrgId: effectiveSubOrgId, lob: effectiveLob });
      setShowCliveWarning(isCliveEnabled);
    } else {
      // CASE 3: No draft, no policy — "Take Loss manually" with empty form + email side panel
      const effectiveLob = lob || 'general_claim';
      const effectiveSubOrgId = subOrgId || undefined;
      setShowCliveWarning(false);
      handleLossWithoutPolicy({ subOrgId: effectiveSubOrgId, lob: effectiveLob });
    }
  };

  // The drawer's FNOL button navigates with no route state, and since the route already matches this
  // instance stays mounted. Without re-syncing, a Create Like context left over from a previous
  // entry would leak into that unrelated report and link the new claim to the wrong source claim.
  const previousRouteCreateLikeContextRef = useRef(location.state?.createLikeContext);
  useLayoutEffect(() => {
    routeCreateLikeContextRef.current = location.state?.createLikeContext;
  }, [location]);
  useEffect(() => {
    const routeCreateLikeContext = location.state?.createLikeContext;
    setCreateLikeContext(routeCreateLikeContext);
    // A form filled for one Create Like entry belongs to that entry only, and NewFnolUI's Formik
    // (no enableReinitialize) would keep its values through the next entry's policy, so the report is
    // dropped and the next entry starts from its own search or source policy.
    if (routeCreateLikeContext !== previousRouteCreateLikeContextRef.current) {
      // A source policy that failed on an earlier entry says nothing about this one's. Within the same
      // entry the load does not run again, so the failure stands.
      setHasExistingPolicyLoadFailed(false);
      setFnolDraft(undefined);
      setSavedFnolDraftId(undefined);
      setChosenPolicyAndClaimValues(null);
      setSelectedSubOrgId(undefined);
      setManuallyFillingInsuredDetails(false);
      setShowLossWithoutPolicyCard(true);
    }
    previousRouteCreateLikeContextRef.current = routeCreateLikeContext;
  }, [location]);

  // The source claim's policy takes the searched policy's place on the same Create Like path, so the
  // draft is fetched and applied exactly as it is after a search.
  useEffect(() => {
    // Once the search has taken over, re-ticking the initial communication does not reload the policy.
    if (!createLikeContext?.useExistingPolicy || isAwaitingExpressions || hasExistingPolicyLoadFailed) {
      return undefined;
    }
    let isCancelled = false;
    const isStillOpen = () => !isCancelled && routeCreateLikeContextRef.current === createLikeContext;
    const selectExistingPolicy = async () => {
      const { policy, missingFieldDescs } = await loadExistingPolicy(createLikeContext.sourcePolicyId);
      if (!isStillOpen()) {
        return;
      }
      if (missingFieldDescs) {
        toast.error(`Cannot use the original policy — missing required fields: ${missingFieldDescs}`);
      }
      // The loss date the search would have been seeded with, so a missing or failed draft still
      // opens on it.
      const claimValues = createLikeContext.sourceDateOfLoss
        ? { date_of_loss: createLikeContext.sourceDateOfLoss }
        : {};
      // Not applied also when an earlier entry's draft fetch still holds the handler: the search
      // takes over rather than leaving the loading indicator up.
      const isApplied = !!policy && (await handleCreateLikeSelectPolicy(policy, claimValues, { isPolicyReused: true }));
      if (!isApplied && isStillOpen()) {
        setHasExistingPolicyLoadFailed(true);
      }
    };
    selectExistingPolicy();
    return () => {
      isCancelled = true;
    };
    // Once per Create Like context, and for a phone rep once the initial communication is confirmed:
    // the handlers it calls are re-created on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createLikeContext, isAwaitingExpressions]);

  const handleInsuredDetailsFilled = async (insuredDetails) => {
    const subOrganization = selectedSubOrgId
      ? userOrganization.sub_organizations.find((subOrganization) => subOrganization.id === selectedSubOrgId)
      : null;

    const lobIncidentConfigurationsDict = subOrganization
      ? subOrganization.lob_incident_configurations_dict
      : userOrganization.lob_incident_configurations_dict;
    const incidentConfiguration = lobIncidentConfigurationsDict[manualPolicyType.replace('_policy', '_claim')];

    const newManualPolicy = {
      type: manualPolicyType,
      lob: manualPolicyLob,
      is_manual: true,
      id: uuidv4(),
      contacts: [insuredDetails],
      organization_id: manualPolicyOrganization.id,
      sub_organization: subOrganization,
      incident_configuration: incidentConfiguration,
      client: manualPolicyOrganization.name,
      insured_contact: insuredDetails,
      insured_contact_id: insuredDetails.id,
      insured_contact_full_name: insuredDetails.full_name,
      insured_property_location: {
        address1: insuredDetails.street_address1,
        address2: insuredDetails.street_address2,
        city: insuredDetails.city,
        county: insuredDetails.county,
        state: insuredDetails.state,
        country: insuredDetails.country,
        zipcode: insuredDetails.zipcode,
      },
      is_claims_made: false, // should be set in the policy screen by the user / policy API search
    };

    // A loss taken without a policy has one only from here on, so this is where its Create Like
    // draft is fetched. It lands in the same update as the policy, as on the search path.
    const draft = createLikeContext ? await fetchCreateLikeDraftForPolicy(newManualPolicy) : null;
    if (draft) {
      setFnolDraft({ draft });
    }
    setChosenPolicyAndClaimValues({ policy: newManualPolicy, claimValues: {} });
    setManuallyFillingInsuredDetails(false);
  };

  if (chosenPolicyAndClaimValues) {
    const chosenPolicy = chosenPolicyAndClaimValues.policy;
    if (!SUPPORTED_POLICIES.includes(chosenPolicy.type)) {
      throw Error(`Unknown policy type: ${chosenPolicy.type}`);
    }

    return (
      <PolicyContextProvider policy={chosenPolicy}>
        <ActivePolicyCategoriesProvider>
          <FnolContactsProvider fnolDraft={fnolDraft}>
            {isConfigurableFnolEnabled && CONFIGURABLE_FNOL_POLICY_TYPES.includes(chosenPolicy.type) && (
              <NewFnolScreen
                onSubmitFnol={handleFnolSubmitted}
                claimValues={chosenPolicyAndClaimValues.claimValues}
                onSubmitDraft={handleSubmitDraft}
                fnolDraft={fnolDraft}
                subOrganizationId={selectedSubOrgId}
                communicationId={communicationId}
                fnolAiRecommendationId={fnolAiRecommendationId}
                showCliveWarning={showCliveWarning}
                mixpanelExtra={mixpanelExtra}
                hasFnolAiRecommendations={hasFnolAiRecommendations}
                createLikeContext={createLikeContext}
              />
            )}
            {!isConfigurableFnolEnabled && chosenPolicy.type === 'auto_policy' && (
              <AutoFnol
                onSubmitFnol={handleFnolSubmitted}
                claimValues={chosenPolicyAndClaimValues.claimValues}
                onSubmitDraft={handleSubmitDraft}
                fnolDraft={fnolDraft}
                subOrganizationId={selectedSubOrgId}
              />
            )}
            {!isConfigurableFnolEnabled && chosenPolicy.type === 'home_policy' && (
              <HomeFnolPage
                onSubmit={handleFnolSubmitted}
                claimValues={chosenPolicyAndClaimValues.claimValues}
                onSubmitDraft={handleSubmitDraft}
                fnolDraft={fnolDraft}
                subOrganizationId={selectedSubOrgId}
              />
            )}
          </FnolContactsProvider>
        </ActivePolicyCategoriesProvider>
      </PolicyContextProvider>
    );
  }

  if (manuallyFillingInsuredDetails) {
    return (
      <InsuredDetailsFormDialog
        onCancel={() => {
          setFnolDraft(undefined);
          setSavedFnolDraftId(undefined);
          setManuallyFillingInsuredDetails(false);
          setShowLossWithoutPolicyCard(true);
        }}
        onInsuredDetailsFilled={handleInsuredDetailsFilled}
      />
    );
  }

  if (manuallyFillingOrganization) {
    return (
      <OrganizationSelectDialog
        isDialog
        onCancel={() => setManuallyFillingOrganization(false)}
        onSelectOrganization={(org) => {
          setManualPolicyOrganization(org);
          setManuallyFillingInsuredDetails(true);
          setManuallyFillingOrganization(false);
        }}
      />
    );
  }

  if (isLoadingExistingPolicy) {
    return <LoadingSwitch isLoading />;
  }

  return (
    <>
      <Grid container justify="center">
        {createLikeContext && (
          <AlertBanner
            className="m-12 w-full"
            alertType={AlertBanner.ALERT_TYPES.INFO}
            withIcon
            title="Create Like Claim"
            note={
              createLikeContext.useExistingPolicy && !hasExistingPolicyLoadFailed
                ? `Creating a claim like ${
                    createLikeContext.sourceClaimIdDisplay || ''
                  } under its policy — confirm the initial communication to continue. The new claim will be linked to the original.`
                : `Creating a claim like ${createLikeContext.sourceClaimIdDisplay || ''} — ${
                    hasExistingPolicyLoadFailed ? 'its policy could not be used, so search' : 'search'
                  } and select the policy this claim should have been created under. The new claim will be linked to the original.`
            }
          />
        )}
        {isUserPhoneRep && (
          <Grid item xs={12}>
            <div className={classes.cardDivRow}>
              <CardDialog title="Initial Communication">
                <ShowOnlyTextField
                  label=""
                  classes={classes}
                  showOnlyValueComponent={INITIAL_COMMUNICATION_TEXT}
                  fullWidth
                  multiline
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={expressionVerified}
                      onChange={(event) => setExpressionVerified(event.target.checked)}
                    />
                  }
                  label={EXPRESSIONS_TEXT}
                />
              </CardDialog>
            </div>
          </Grid>
        )}
        {hasPolicySource(policySearchConfig) && (
          <Grid item xs={12}>
            <div className={classes.cardDivRow}>
              <PolicySearchContainer
                disabled={(!expressionVerified && isUserPhoneRep) || isFetchingDraft}
                initialSearchValues={
                  // Seed (editable) the loss date the policy has to be in force at — it is the
                  // source claim's, and the search is for the policy that claim should have had.
                  createLikeContext?.sourceDateOfLoss ? { date_of_loss: createLikeContext.sourceDateOfLoss } : undefined
                }
                enableSelectingRelatedClaim
                choosePolicyFromRelatedClaimsText="Report New Loss"
                onSelectPolicy={createLikeContext ? handleCreateLikeSelectPolicy : handleSelectPolicy}
                onPolicyNotFound={(policyType) => handlePolicyNotFound(policyType)}
                policyTypes={userOrganization.supported_claim_types.map((claimType) =>
                  claimType.replace('_claim', '_policy')
                )}
                onFnolDraftSelected={(fnolDraft) => handleDraftSelected(fnolDraft, 'related_to_policy')}
                allowSearchByAddress
              />
            </div>
          </Grid>
        )}
        {showLossWithoutPolicyCard && (
          <Grid item xs={12}>
            <div className={classes.cardDivRow}>
              <LossWithoutPolicyCard onTakeLoss={handleLossWithoutPolicy} />
            </div>
          </Grid>
        )}
        {isConfigurableFnolEnabled && !createLikeContext && (
          <Grid item xs={12}>
            <div className={classes.cardDivRow}>
              <FnolDraftsContainer onSelectDraft={(fnolDraft) => handleDraftSelected(fnolDraft, 'all_drafts')} />
            </div>
          </Grid>
        )}
        {isFnolInboxConfigured && isFnolAiInboxAllowedForUser && !createLikeContext && (
          <Grid item xs={12}>
            <div className={classes.cardDivRow}>
              <FnolAiInboxContainer onNewLoss={handleFnolAiInboxNewLoss} onExpand={onFnolInboxExpand} />
            </div>
          </Grid>
        )}
      </Grid>
    </>
  );
}

function InsuredDetailsFormDialog(props) {
  const { onCancel, onInsuredDetailsFilled } = props;
  // CreateContactDialog calls onClose both when the user dismisses it and when it closes itself
  // after a successful create. Only the first is a cancellation, so the two are separated here:
  // wiring onClose straight to onCancel discards the FNOL draft on the success path as well.
  const submittedRef = useRef(false);

  return (
    <CreateContactDialog
      onClose={() => {
        if (submittedRef.current) {
          return;
        }
        onCancel();
      }}
      onContactCreate={async (contact) => {
        // After the await: UpsertContactFormik leaves the dialog open when onSubmit rejects, and a
        // ref set early would swallow every later close, stranding the user with a dead cancel.
        await onInsuredDetailsFilled(contact);
        submittedRef.current = true;
      }}
      title="Fill Insured Details"
      acceptedRoles={['insured']}
      editingRoleDisabled
    />
  );
}

InsuredDetailsFormDialog.propTypes = {
  onInsuredDetailsFilled: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

FnolScreen.propTypes = {
  onFnolInboxExpand: PropTypes.func,
};

export default FnolScreen;
export { InsuredDetailsFormDialog };
